import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Pencil, Plus, StickyNote, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { RICH_TEXT_CLASSES, RichTextEditor } from './RichTextEditor'
import { useNotes } from '../hooks/useNotes'
import { apiErrorDescription } from '@/services/core/apiError'
import type { Note, NoteParentType } from '@/services/shared/notesApi'
import { htmlToText, sanitizeHtml } from '../utils/sanitizeHtml'
import { formatDateTime } from '../utils/format'

/** A note's body: the API stores it line by line, the editor works on one string. */
function noteHtml(note: Note): string {
  return note.data.join(note.dataType === 'HTML' ? '' : '<br>')
}

function formatStamp(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatDateTime(d, { dateStyle: 'short', timeStyle: 'short' })
}

interface NotesSectionProps {
  /** Which kind of object the notes hang off. */
  parent: NoteParentType
  parentId: string | undefined
  /** The parent's note ids, as the API returned them. */
  noteIds: string[]
  /** Query key of the parent object, refetched after a note is written. */
  parentQueryKey: readonly unknown[]
  /** Hides the new/edit/remove controls — for a read-only view of an object. */
  readOnly?: boolean
}

/**
 * The notes on one object: newest first, each editable in place through a small
 * WYSIWYG. Writes go straight to the API rather than into the parent form, so
 * the section works the same on a page with unsaved changes as on one without.
 * It has no card of its own; it sits inside another card, such as the latest
 * activity card.
 */
export function NotesSection({ parent, parentId, noteIds, parentQueryKey, readOnly }: NotesSectionProps) {
  const { t } = useTranslation()
  const { notes, isLoading, create, update, remove } = useNotes(parent, parentId, noteIds, parentQueryKey)

  /** Which note is open in the editor: a note id, `new`, or nothing. */
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [pendingDelete, setPendingDelete] = useState<Note | null>(null)

  const isSaving = create.isPending || update.isPending

  const openNew = () => {
    setDraft('')
    setEditing('new')
  }

  const openEdit = (note: Note) => {
    setDraft(noteHtml(note))
    setEditing(note.id)
  }

  const close = () => {
    setEditing(null)
    setDraft('')
  }

  const failed = (error: unknown) => toast.error(t('notes.saveFailed'), { description: apiErrorDescription(error) })

  const save = () => {
    const html = sanitizeHtml(draft)
    // An empty body would leave a blank row behind, so it just closes the editor.
    if (!htmlToText(html)) return close()
    const action =
      editing === 'new'
        ? create.mutateAsync(html)
        : update.mutateAsync({ id: editing!, html })
    action.then(close).catch(failed)
  }

  const confirmDelete = () => {
    const note = pendingDelete
    setPendingDelete(null)
    if (!note) return
    remove.mutateAsync(note.id).catch(failed)
  }

  const editor = (
    <div className="flex flex-col gap-2">
      {/* Keyed on the target so switching between notes remounts the editor
          with the right body rather than reseeding a live one. */}
      <RichTextEditor
        key={editing}
        value={draft}
        onChange={setDraft}
        placeholder={t('notes.placeholder')}
        autoFocus
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={close} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={save} loading={isSaving}>
          {t('notes.saveNote')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <StickyNote className="size-4" />
          {t('notes.title')}
          {notes.length > 0 && (
            <span className="rounded-full bg-(--ad-neutral-soft) px-2 py-px text-xs font-semibold text-(--ad-text-muted)">
              {notes.length}
            </span>
          )}
        </span>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={openNew}
            disabled={!parentId || editing === 'new'}
          >
            <Plus />
            {t('notes.newNote')}
          </Button>
        )}
      </div>

      {editing === 'new' && editor}

      {isLoading && <Skeleton className="h-14 w-full" />}

      {!isLoading && notes.length === 0 && editing !== 'new' && (
        <p className="text-sm text-(--ad-text-muted)">{t('notes.empty')}</p>
      )}

      {notes.map((note) =>
        editing === note.id ? (
          <div key={note.id}>{editor}</div>
        ) : (
          <div
            key={note.id}
            className="flex items-start gap-2 rounded-md bg-(--ad-neutral-soft) px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs text-(--ad-text-muted)">
                <span className="font-semibold text-(--ad-text)">
                  {note.createUser?.name || t('notes.unknownAuthor')}
                </span>
                {formatStamp(note.createTime) && `, ${formatStamp(note.createTime)}`}
                {note.updateTime > note.createTime && ` · ${t('notes.edited')}`}
              </div>
              <div
                className={RICH_TEXT_CLASSES}
                // Note bodies are stored as posted, so they are reduced to the
                // allowed tag set on the way out as well as on the way in.
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(noteHtml(note)) }}
              />
            </div>
            {!readOnly && (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.edit')}
                  onClick={() => openEdit(note)}
                >
                  <Pencil />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.delete')}
                  onClick={() => setPendingDelete(note)}
                >
                  <Trash2 />
                </Button>
              </div>
            )}
          </div>
        )
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notes.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('notes.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
